import { forwardRef, useEffect, useState } from "react";
import type { HTMLAttributes } from "react";
import { usePreviousValue } from "../hooks/usePreviousValue";

export type Direction = "up" | "down";

export type UpOrDownProps = HTMLAttributes<HTMLDivElement> & {
  value: number;
  animationLength?: number | null;
  directionOverride?: Direction;
};

// Direction plus a per-change timestamp: a same-direction move must still
// be a distinct state value, otherwise the set bails out of React state and
// the previous change's clear-timer keeps running — cutting the new
// indicator short (or clearing it instantly when the moves straddle the
// window edge).
type Pulse = { direction: Direction; at: number };

export const UpOrDown = forwardRef<HTMLDivElement, UpOrDownProps>(
  (
    { value, animationLength = 3000, directionOverride, children, ...props },
    ref,
  ) => {
    const [pulse, setPulse] = useState<Pulse | undefined>(
      directionOverride ? { direction: directionOverride, at: 0 } : undefined,
    );
    const prevValue = usePreviousValue(value);
    const prevAnimLength = usePreviousValue(animationLength);

    useEffect(() => {
      // no real price (dash / odds <= 1) — don't animate
      if (value <= 1) {
        setPulse(undefined);
        return;
      }

      if (!prevValue) return;

      if (prevValue !== value) {
        setPulse({
          direction: prevValue < value ? "up" : "down",
          at: Date.now(),
        });
        return;
      }

      if (animationLength === null) return;

      if (prevAnimLength === null && animationLength >= 0) {
        setPulse(undefined);
      }
    }, [value, animationLength, prevValue, prevAnimLength]);

    useEffect(() => {
      if (!pulse || animationLength === null) return;
      const timeout = setTimeout(() => setPulse(undefined), animationLength);
      return () => clearTimeout(timeout);
    }, [pulse, animationLength]);

    return (
      <div ref={ref} {...props} data-status={pulse?.direction}>
        {children}
      </div>
    );
  },
);

UpOrDown.displayName = "UpOrDown";
